import { CartUpdateEvent } from "@theme/events";
import { fetchConfig } from "@theme/utilities";

/** Selected line-item keys, kept at module scope so they survive section morphs. */
const selectedKeys = new Set();

class CustomCartSelection extends HTMLElement {
  connectedCallback() {
    this.resync();
  }

  get sectionId() {
    return (
      this.closest("[data-section-id]")?.getAttribute("data-section-id") ??
      "custom-cart-drawer"
    );
  }

  /** All selectable (non-nested) line item checkboxes currently rendered. */
  get itemCheckboxes() {
    return /** @type {HTMLInputElement[]} */ (
      Array.from(this.querySelectorAll("[data-ccd-item]"))
    );
  }

  /**
   * Reconcile the checkbox DOM + counter + button state with `selectedKeys`.
   * Safe to call repeatedly (e.g. after every morph).
   */
  resync = () => {
    const boxes = this.itemCheckboxes;
    const liveKeys = new Set(boxes.map((box) => box.dataset.lineKey));

    for (const key of selectedKeys) {
      if (!liveKeys.has(key)) selectedKeys.delete(key);
    }

    for (const box of boxes) {
      box.checked = selectedKeys.has(box.dataset.lineKey);
      box.closest(".ccd-item")?.classList.toggle("is-selected", box.checked);
    }

    const total = boxes.length;
    const selected = boxes.filter((box) => box.checked).length;

    const counter = this.querySelector("[data-ccd-count]");
    if (counter) {
      const noun =
        total === 1
          ? this.dataset.itemWord || "item"
          : this.dataset.itemsWord || "items";
      counter.textContent = `${selected}/${total} ${noun} selected`;
    }

    const selectAll = this.querySelector("[data-ccd-select-all]");
    if (selectAll instanceof HTMLInputElement) {
      selectAll.checked = total > 0 && selected === total;
      selectAll.indeterminate = selected > 0 && selected < total;
    }

    const deleteBtn = this.querySelector("[data-ccd-delete-selected]");
    if (deleteBtn instanceof HTMLButtonElement) {
      deleteBtn.disabled = selected === 0 || this.hasAttribute("data-busy");
    }
  };

  /** @param {Event} event */
  handleChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.matches("[data-ccd-select-all]")) {
      for (const box of this.itemCheckboxes) {
        if (target.checked) selectedKeys.add(box.dataset.lineKey);
        else selectedKeys.delete(box.dataset.lineKey);
      }
      this.resync();
      return;
    }

    if (target.matches("[data-ccd-item]")) {
      if (target.checked) selectedKeys.add(target.dataset.lineKey);
      else selectedKeys.delete(target.dataset.lineKey);
      this.resync();
    }
  }

  /** @param {Event} event */
  async handleClick(event) {
    const trigger =
      event.target instanceof Element
        ? event.target.closest("[data-ccd-delete-selected]")
        : null;
    if (!trigger) return;

    event.preventDefault();

    const keys = [...selectedKeys];
    if (keys.length === 0 || this.hasAttribute("data-busy")) return;

    this.setAttribute("data-busy", "");
    this.resync();

    /** @type {Record<string, number>} */
    const updates = {};
    for (const key of keys) updates[key] = 0;

    try {
      const response = await fetch(
        Theme.routes.cart_update_url,
        fetchConfig("json", {
          body: JSON.stringify({
            updates,
            sections: this.sectionId,
            sections_url: window.location.pathname,
          }),
        })
      );

      const data = await response.json();
      selectedKeys.clear();

      document.dispatchEvent(
        new CartUpdateEvent(data, "custom-cart-selection", {
          source: "custom-cart-selection",
          itemCount: data.item_count,
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error(
        "[custom-cart-drawer] failed to delete selected items",
        error
      );
    } finally {
      this.removeAttribute("data-busy");
      requestAnimationFrame(this.resync);
    }
  }
}

if (!customElements.get("custom-cart-selection")) {
  customElements.define("custom-cart-selection", CustomCartSelection);
}

/** @param {Event} event @returns {CustomCartSelection | null} */
const hostFrom = (event) =>
  event.target instanceof Element
    ? /** @type {CustomCartSelection | null} */ (
        event.target.closest("custom-cart-selection")
      )
    : null;

// Delegated listeners - resilient to the drawer being re-rendered by morph.
document.addEventListener("change", (event) =>
  hostFrom(event)?.handleChange(event)
);
document.addEventListener("click", (event) =>
  hostFrom(event)?.handleClick(event)
);

// After any cart update elsewhere (quantity change, add-to-cart, discount…), the
// section may be re-rendered by <cart-items-component>. Re-apply selection state
// once that markup has settled.
const resyncSelection = () => {
  const host = document.querySelector("custom-cart-selection");
  if (host instanceof CustomCartSelection) host.resync();
};

document.addEventListener("cart:update", () => {
  requestAnimationFrame(resyncSelection);
  setTimeout(resyncSelection, 60);
});
